/**
 * useConsents — GDPR consent state for a patient (therapist dashboard).
 *
 * Wraps the therapist consent API:
 *   GET  /patients/{id}/consents          → state + append-only history
 *   POST /patients/{id}/consents/attest   → record paper/verbal attestation
 *
 * Multiple hook instances (ConsentBadge, session Toolbar) stay in sync via a
 * window event fired after every successful attestation.
 */
import { useCallback, useEffect, useState } from "react";
// NOTE: import i18next directly (not ../i18n) — the app i18n module runs
// init() at import time, which breaks consumers in unit tests.
import i18next from "i18next";
import { useAuth } from "../auth/AuthProvider";
import { apiGet, apiPost } from "../lib/api-client";

export const CONSENT_PURPOSES = ["recording", "ai_analysis", "wearables"] as const;
export type ConsentPurpose = (typeof CONSENT_PURPOSES)[number];
export type ConsentAction = "granted" | "revoked";
export type ConsentChannel = "mobile_app" | "dashboard_attested";

export type ConsentState = {
  recording: boolean;
  ai_analysis: boolean;
  wearables: boolean;
};

export type ConsentEvent = {
  purpose: string;
  action: ConsentAction;
  created_at: string;
  channel: ConsentChannel;
  policy_version: string;
};

export type ConsentsResponse = {
  patient_id: string;
  policy_version: string;
  state: ConsentState;
  history: ConsentEvent[];
};

type AttestResponse = {
  ok: boolean;
  state: ConsentState;
};

/** Fired on window after a successful attestation so all instances refresh. */
const CONSENTS_UPDATED_EVENT = "affektra:consents-updated";

export type UseConsentsResult = {
  data: ConsentsResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  attest: (purpose: ConsentPurpose, action: ConsentAction) => Promise<AttestResponse>;
};

export function useConsents(patientId?: string): UseConsentsResult {
  const { ensureToken } = useAuth();

  const [data, setData] = useState<ConsentsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getToken = useCallback(async () => {
    const tk = await ensureToken();
    if (!tk) throw new Error("No auth token");
    return tk;
  }, [ensureToken]);

  const refresh = useCallback(async () => {
    if (!patientId) {
      setData(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await apiGet<ConsentsResponse>(
        `/patients/${encodeURIComponent(patientId)}/consents`,
        getToken
      );
      setData(res);
    } catch (e) {
      console.error("[useConsents] failed to load consent state:", e);
      setData(null);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [patientId, getToken]);

  // Initial load + reload on patient change
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Stay in sync with attestations made elsewhere (e.g. badge dialog vs toolbar)
  useEffect(() => {
    if (!patientId) return;
    const onUpdated = (ev: Event) => {
      const detail = (ev as CustomEvent<{ patientId?: string }>).detail;
      if (detail?.patientId === patientId) void refresh();
    };
    window.addEventListener(CONSENTS_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(CONSENTS_UPDATED_EVENT, onUpdated);
  }, [patientId, refresh]);

  const attest = useCallback(
    async (purpose: ConsentPurpose, action: ConsentAction): Promise<AttestResponse> => {
      if (!patientId) throw new Error("No patient selected");
      if (!data) throw new Error("Consent state not loaded yet");
      const res = await apiPost<AttestResponse>(
        `/patients/${encodeURIComponent(patientId)}/consents/attest`,
        {
          purpose,
          action,
          policy_version: data.policy_version,
          locale: i18next.language === "hu" ? "hu" : "en",
        },
        getToken
      );
      window.dispatchEvent(
        new CustomEvent(CONSENTS_UPDATED_EVENT, { detail: { patientId } })
      );
      return res;
    },
    [patientId, data, getToken]
  );

  return { data, loading, error, refresh, attest };
}
